import * as React from 'react';
import * as v from 'valibot';
import { useLingui } from '@lingui/react/macro';

/**
 * The Lingui texts valibot's global message table renders; a message that is not a generic constraint is
 * written at its check site, from `portal-messages.ts`. Global and process-wide, so client-only. How the
 * registrations map: docs/notes/valibot-and-zod.md
 */
export function useFormI18n() {
	const { t } = useLingui();

	React.useEffect(() => {
		// A schema-kind issue is a wrong or missing type. In a form that almost always means the field was
		// left empty (string, number, boolean, enum, date…), so it reads as "Required" rather than
		// valibot's "Invalid type: Expected string but received undefined".
		v.setSchemaMessage(() => t`Required`);

		// Each message binds the interpolated value to a local first: Lingui's extractor only accepts a
		// bare `${variable}`, not `${issue.requirement}` or `${String(...)}`.
		v.setSpecificMessage(v.minLength, (issue) => {
			const min = String(issue.requirement);
			return issue.requirement === 1 ? t`Required` : t`Minimum ${min} characters`;
		});
		v.setSpecificMessage(v.maxLength, (issue) => {
			const max = String(issue.requirement);
			return t`Maximum ${max} characters`;
		});
		v.setSpecificMessage(v.minValue, (issue) => {
			const min = String(issue.requirement);
			return t`Minimum ${min}`;
		});
		v.setSpecificMessage(v.maxValue, (issue) => {
			const max = String(issue.requirement);
			return t`Maximum ${max}`;
		});
		// Arrays are capped with `maxLength`, so `maxSize` only ever measures a File — a limit set in
		// bytes but only ever read as MB or GB, which is how the upload fields word their own hints.
		v.setSpecificMessage(v.maxSize, (issue) => {
			const bytes = issue.requirement;
			const inGB = bytes / 1024 / 1024 / 1024;
			const inMB = bytes / 1024 / 1024;
			const size = inGB >= 1 ? `${inGB} GB` : `${inMB} MB`;
			return t`File must not exceed ${size}`;
		});
		v.setSpecificMessage(v.email, () => t`Invalid email`);
		v.setSpecificMessage(v.url, () => t`Invalid URL`);
		v.setSpecificMessage(v.isoDate, () => t`Invalid date`);
		v.setSpecificMessage(v.isoDateTime, () => t`Invalid date`);
		// `v.literal(true)` is a consent checkbox — only the terms-and-conditions fields use it. Any
		// other literal falls through to the schema message.
		v.setSpecificMessage(v.literal, (issue) =>
			issue.expected === 'true' ? t`You must accept the terms and conditions` : t`Required`,
		);
	}, [t]);
}
